import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Plus, Trash2, Upload, RefreshCw, Radio as RadioIcon, Mic, ExternalLink, Music, Sparkles, X, GripVertical, CheckCircle2, Headphones } from 'lucide-react';
import { toast } from 'sonner';

/* eslint-disable react-hooks/exhaustive-deps */

// Hand-curated list of free music sources tailored to Kickass Course's vibe.
const FREE_MUSIC_SOURCES = [
  {
    name: 'Pixabay Music',
    url: 'https://pixabay.com/music/search/cinematic%20motivation/',
    desc: 'Free MP3s, no attribution. Search for "cinematic motivation" or "dark gritty".',
    badge: 'Best free',
  },
  {
    name: 'YouTube Audio Library',
    url: 'https://studio.youtube.com/channel/UC/music',
    desc: 'Google\u2019s royalty-free pool. Download MP3 directly.',
  },
  {
    name: 'Uppbeat',
    url: 'https://uppbeat.io/browse/music/cinematic',
    desc: 'Free with account. Cinematic and modern.',
  },
  {
    name: 'Free Music Archive',
    url: 'https://freemusicarchive.org/genre/Instrumental/',
    desc: 'Indie, ambient, experimental \u2014 mostly Creative Commons.',
  },
  {
    name: 'Mixkit',
    url: 'https://mixkit.co/free-stock-music/',
    desc: 'Free for any project. Filter by mood.',
  },
];

const MOOD_SUGGESTIONS = ['calm', 'grit', 'focus', 'storm', 'hope', 'shadow'];

const fileUrl = (u) => {
  if (!u) return '';
  if (/^https?:/i.test(u)) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${u}`;
};

function dragInactive(e) {
  e.preventDefault();
  e.stopPropagation();
}

export default function RadioManager() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ url: '', title: '', mood: '' });
  const [dragHover, setDragHover] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/settings/radio');
      setCfg(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load radio config.');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put('/admin/settings/radio', patch);
      setCfg(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => save({ [key]: value });

  const updateTrackLocal = (idx, patch) => {
    setCfg((c) => {
      const tracks = [...(c.tracks || [])];
      tracks[idx] = { ...tracks[idx], ...patch };
      return { ...c, tracks };
    });
  };
  const commitTracks = () => save({ tracks: cfg.tracks });

  const removeTrack = (idx) => {
    if (!window.confirm('Remove this track from the station?')) return;
    const tracks = (cfg.tracks || []).filter((_, i) => i !== idx);
    save({ tracks }).then(() => toast.success('Track removed.'));
  };

  const moveTrack = (idx, dir) => {
    const tracks = [...(cfg.tracks || [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= tracks.length) return;
    [tracks[idx], tracks[ni]] = [tracks[ni], tracks[idx]];
    save({ tracks });
  };

  const submitAddForm = async () => {
    const url = (addForm.url || '').trim();
    if (!url) { setError('A URL is required.'); return; }
    const title = (addForm.title || '').trim() || prettyTitleFromUrl(url);
    const mood = (addForm.mood || '').trim();
    const tracks = [...(cfg.tracks || []), { id: cryptoUuid(), title, url, mood }];
    await save({ tracks });
    toast.success(`Added "${title}".`);
    setAddForm({ url: '', title: '', mood: '' });
    setShowAddForm(false);
  };

  // Multi-file upload — drag a whole folder or pick many files.
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    const audioFiles = Array.from(files).filter((f) => f.type.startsWith('audio/'));
    if (audioFiles.length === 0) {
      setError('No audio files detected. Drop MP3, WAV, M4A, OGG, or FLAC files.');
      return;
    }
    setUploading(true);
    setError('');
    const newTracks = [];
    for (let i = 0; i < audioFiles.length; i++) {
      const file = audioFiles[i];
      setUploadProgress(`Uploading ${i + 1} of ${audioFiles.length}: ${file.name}`);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/admin/radio/upload-track', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const title = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
        newTracks.push({ id: cryptoUuid(), title, url: data.url, mood: '' });
      } catch (e) {
        toast.error(`Failed to upload ${file.name}: ${e?.response?.data?.detail || e.message}`);
      }
    }
    setUploadProgress('');
    if (newTracks.length > 0) {
      const tracks = [...(cfg.tracks || []), ...newTracks];
      await save({ tracks });
      toast.success(`Added ${newTracks.length} track${newTracks.length === 1 ? '' : 's'}.`);
    }
    setUploading(false);
  };

  // Drag-and-drop handlers.
  const onDragEnter = (e) => { dragInactive(e); setDragHover(true); };
  const onDragOver = (e) => { dragInactive(e); setDragHover(true); };
  const onDragLeave = (e) => { dragInactive(e); setDragHover(false); };
  const onDrop = (e) => {
    dragInactive(e);
    setDragHover(false);
    const files = e.dataTransfer?.files;
    if (files && files.length) uploadFiles(files);
  };

  if (!cfg) return <div className="text-muted-kc text-sm" data-testid="radio-mgr-loading">Loading station…</div>;

  const tracks = cfg.tracks || [];

  return (
    <div className="space-y-6" data-testid="radio-manager">
      <div>
        <h2 className="font-display uppercase text-3xl md:text-4xl inline-flex items-center gap-2"><RadioIcon size={22} className="text-[color:var(--kc-gold)]"/> Pirate Radio</h2>
        <p className="text-muted-kc text-sm mt-1">Drop MP3s in. The AI DJ talks between tracks. That's it.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-[color:var(--kc-danger)] text-[color:var(--kc-danger)] rounded-[var(--kc-radius-sm)] p-3 text-sm flex items-start justify-between gap-3" data-testid="radio-mgr-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="text-[color:var(--kc-danger)]/70 hover:text-[color:var(--kc-danger)]"><X size={14}/></button>
        </div>
      )}

      {/* ===== 1. GET MUSIC — free sources card ===== */}
      <div className="bg-gradient-to-r from-[color:var(--kc-gold)]/8 to-transparent border border-[color:var(--kc-gold)]/30 rounded-[var(--kc-radius-md)] p-5" data-testid="radio-mgr-free-sources">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-[color:var(--kc-gold)]/15 border border-[color:var(--kc-gold)]/40 inline-flex items-center justify-center text-[color:var(--kc-gold)] flex-shrink-0 mt-0.5">
            <Sparkles size={16}/>
          </span>
          <div className="flex-1">
            <div className="font-display uppercase text-base">Need music? Grab it free from any of these</div>
            <p className="text-xs text-muted-kc mt-0.5">Click a source &rarr; download MP3s &rarr; drop them into the upload zone below. Done.</p>
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {FREE_MUSIC_SOURCES.map((src) => (
            <a
              key={src.name}
              href={src.url}
              target="_blank"
              rel="noreferrer"
              className="group bg-white border border-kc hover:border-[color:var(--kc-gold)] hover:bg-[color:var(--kc-gold)]/5 transition-all rounded-[var(--kc-radius-sm)] p-3"
              data-testid={`radio-mgr-source-${src.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-display uppercase text-sm tracking-wide inline-flex items-center gap-1.5">
                  <Music size={12} className="text-[color:var(--kc-gold)] flex-shrink-0"/>
                  {src.name}
                </div>
                <ExternalLink size={11} className="text-muted-kc opacity-60 group-hover:opacity-100 flex-shrink-0 mt-1"/>
              </div>
              <div className="text-[11px] text-muted-kc mt-1 leading-snug">{src.desc}</div>
              {src.badge && (
                <div className="mt-2 inline-block text-[9px] tracking-[0.18em] uppercase font-semibold text-black bg-[color:var(--kc-gold)] px-1.5 py-0.5 rounded">{src.badge}</div>
              )}
            </a>
          ))}
        </div>
      </div>

      {/* ===== 2. UPLOAD — drag-and-drop zone ===== */}
      <div
        className={`rounded-[var(--kc-radius-md)] border-2 border-dashed transition-all duration-200 ${dragHover ? 'border-[color:var(--kc-gold)] bg-[color:var(--kc-gold)]/8 scale-[1.005]' : 'border-kc-strong bg-surface-2'}`}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="radio-mgr-drop-zone"
      >
        <div className="p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[color:var(--kc-gold)]/15 border border-[color:var(--kc-gold)]/40 inline-flex items-center justify-center text-[color:var(--kc-gold)] mb-3">
            <Upload size={22}/>
          </div>
          <div className="font-display uppercase text-xl">Drop MP3s here</div>
          <p className="text-sm text-muted-kc mt-1">Or pick files from your computer. Multiple files at once works.</p>
          <p className="text-[11px] text-muted-kc mt-1">Accepts MP3, WAV, M4A, OGG, FLAC · Up to 50 MB per file.</p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <input ref={fileRef} type="file" accept="audio/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="radio-mgr-upload-input"/>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="h-11 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-40 transition-colors"
              data-testid="radio-mgr-pick-files"
            >
              {uploading ? <><RefreshCw size={14} className="animate-spin"/> Uploading…</> : <><Upload size={14}/> Choose files</>}
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm((v) => !v)}
              disabled={uploading}
              className="h-11 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-kc-strong font-semibold hover:bg-white transition-colors text-sm"
              data-testid="radio-mgr-toggle-add-form"
            >
              <Plus size={14}/> Add by URL instead
            </button>
          </div>
          {uploadProgress && <div className="mt-3 text-xs text-muted-kc" data-testid="radio-mgr-upload-progress">{uploadProgress}</div>}
        </div>

        {/* Inline Add-by-URL form */}
        {showAddForm && (
          <div className="border-t border-kc px-6 py-4 bg-white" data-testid="radio-mgr-add-form">
            <div className="grid md:grid-cols-[2fr_1.5fr_1fr_auto] gap-2">
              <input
                type="url"
                placeholder="Paste MP3 URL (https://… .mp3)"
                value={addForm.url}
                onChange={(e) => setAddForm({ ...addForm, url: e.target.value })}
                className="kc-input2 text-sm"
                data-testid="radio-mgr-add-url"
                autoFocus
              />
              <input
                type="text"
                placeholder="Track title (optional)"
                value={addForm.title}
                onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                className="kc-input2 text-sm"
                data-testid="radio-mgr-add-title"
              />
              <input
                type="text"
                placeholder="Mood (optional)"
                value={addForm.mood}
                onChange={(e) => setAddForm({ ...addForm, mood: e.target.value })}
                list="mood-suggestions"
                className="kc-input2 text-sm"
                data-testid="radio-mgr-add-mood"
              />
              <div className="flex gap-1">
                <button type="button" onClick={submitAddForm} disabled={!addForm.url.trim()} className="h-10 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold hover:bg-black/90 disabled:opacity-30" data-testid="radio-mgr-add-submit">Add</button>
                <button type="button" onClick={() => { setShowAddForm(false); setAddForm({ url: '', title: '', mood: '' }); }} className="h-10 w-10 inline-flex items-center justify-center rounded-[var(--kc-radius-sm)] border border-kc-strong" aria-label="Cancel"><X size={14}/></button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== 3. CURRENT PLAYLIST ===== */}
      <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="font-display uppercase text-xl">Playlist</div>
            <p className="text-[11px] text-muted-kc">{tracks.length} track{tracks.length === 1 ? '' : 's'} · plays in order, or shuffled.</p>
          </div>
          {saving && <span className="text-xs text-muted-kc inline-flex items-center gap-1"><RefreshCw size={12} className="animate-spin"/> Saving…</span>}
        </div>

        {tracks.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-kc">
            <Headphones size={28} className="mx-auto mb-2 opacity-50"/>
            No tracks yet. Drop your first MP3 above to start the station.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="radio-mgr-tracks">
            {tracks.map((t, i) => (
              <li key={t.id} className="bg-surface-2 border border-kc rounded-[var(--kc-radius-sm)] p-3 flex items-center gap-3" data-testid={`radio-mgr-track-${i}`}>
                <div className="flex flex-col text-muted-kc">
                  <button type="button" onClick={() => moveTrack(i, -1)} disabled={i === 0} className="hover:text-ink disabled:opacity-20 leading-none text-[11px]" aria-label="Move up">▲</button>
                  <button type="button" onClick={() => moveTrack(i, 1)} disabled={i === tracks.length - 1} className="hover:text-ink disabled:opacity-20 leading-none text-[11px]" aria-label="Move down">▼</button>
                </div>
                <div className="font-display text-base text-gold w-7 text-center flex-shrink-0">{String(i + 1).padStart(2, '0')}</div>
                <div className="flex-1 grid sm:grid-cols-[1.6fr_1fr] gap-2">
                  <input
                    value={t.title}
                    onChange={(e) => updateTrackLocal(i, { title: e.target.value })}
                    onBlur={commitTracks}
                    placeholder="Track title"
                    className="kc-input2 text-sm"
                    data-testid={`radio-mgr-track-title-${i}`}
                  />
                  <input
                    value={t.mood || ''}
                    onChange={(e) => updateTrackLocal(i, { mood: e.target.value })}
                    onBlur={commitTracks}
                    placeholder="Mood (calm, grit, hope…)"
                    list="mood-suggestions"
                    className="kc-input2 text-sm"
                    data-testid={`radio-mgr-track-mood-${i}`}
                  />
                </div>
                <audio src={fileUrl(t.url)} controls className="h-9 max-w-[240px]"/>
                <button type="button" onClick={() => removeTrack(i)} className="p-2 hover:bg-red-50 text-[color:var(--kc-danger)] rounded-[var(--kc-radius-sm)]" aria-label="Remove" data-testid={`radio-mgr-track-del-${i}`}>
                  <Trash2 size={14}/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ===== 4. STATION SETTINGS (collapsible-ish, smaller) ===== */}
      <details className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5" data-testid="radio-mgr-settings-section">
        <summary className="cursor-pointer font-display uppercase text-lg inline-flex items-center gap-2"><Mic size={16} className="text-[color:var(--kc-gold)]"/> Station & DJ settings</summary>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Station name</label>
            <input value={cfg.station_name || ''} onChange={(e) => setCfg({ ...cfg, station_name: e.target.value })} onBlur={(e) => setField('station_name', e.target.value)} className="kc-input2" data-testid="radio-mgr-name"/>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Tagline</label>
            <input value={cfg.tagline || ''} onChange={(e) => setCfg({ ...cfg, tagline: e.target.value })} onBlur={(e) => setField('tagline', e.target.value)} className="kc-input2" data-testid="radio-mgr-tagline"/>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">DJ frequency (every N tracks)</label>
            <input type="number" min={1} max={10} value={cfg.dj_frequency || 2} onChange={(e) => setCfg({ ...cfg, dj_frequency: parseInt(e.target.value || 2) })} onBlur={(e) => setField('dj_frequency', parseInt(e.target.value || 2))} className="kc-input2 max-w-[120px]" data-testid="radio-mgr-djfreq"/>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!cfg.shuffle} onChange={(e) => setField('shuffle', e.target.checked)} data-testid="radio-mgr-shuffle"/>
              Shuffle order
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1 inline-flex items-center gap-1.5"><Mic size={12} className="text-[color:var(--kc-gold)]"/> DJ persona</label>
            <textarea rows={3} value={cfg.dj_persona || ''} onChange={(e) => setCfg({ ...cfg, dj_persona: e.target.value })} onBlur={(e) => setField('dj_persona', e.target.value)} className="kc-input2 font-mono text-xs" data-testid="radio-mgr-persona"/>
            <p className="text-[11px] text-muted-kc mt-1">System prompt for the AI DJ — controls voice/tone of the commentary between tracks.</p>
          </div>
        </div>
      </details>

      <datalist id="mood-suggestions">
        {MOOD_SUGGESTIONS.map((m) => <option key={m} value={m}/>)}
      </datalist>
    </div>
  );
}

// --- helpers ---
function cryptoUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'id-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}
function prettyTitleFromUrl(url) {
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop() || 'Track';
    return last.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ');
  } catch {
    return 'Track';
  }
}
